import {Component, ElementRef, EventEmitter, inject, Input, OnDestroy, OnInit, Output, Renderer2, ViewChild} from '@angular/core';
import { AuthService } from '../../../Services/auth.service';
import {asyncScheduler, fromEvent, merge, Observable, Subject, Subscription, timer} from 'rxjs';
import {filter, observeOn, switchMap, takeUntil} from 'rxjs/operators';
import { StorageService } from '../../../Services/storage.service';
import { PermissionsService } from '../../../Services/permissions.service';
import {DataService} from '../../../Services/data.service';
import { AppConfigService } from '../../../Services/app-config.service';
import { environment } from '../../../../environments/environment';
import { ResponseModel } from '../../../DataModels/response-model';
import { NotificationService } from '../../../Services/notification.service';
import { EventService} from '../../../Services/event-service';
import {Language} from '../../../DataModels/language.model';
import {SysUserModel} from '../../../DataModels/sys-user-model';
import {DOCUMENT} from '@angular/common';
import {NavigationEnd, Router} from '@angular/router';
import {ChatService} from '../../../Services/chat.service';
import {ChatPreviewItem} from '../chat/chat-thread/chat-thread.model';
import {CompanyAccessModel} from '../../../DataModels/sys-user-model';
import {Modal} from 'bootstrap';

type FullScreenTypes = {
  requestFullscreen?: () => Promise<void>
  mozRequestFullScreen?: () => Promise<void>
  mozCancelFullScreen?: () => Promise<void>
  msExitFullscreen?: () => Promise<void>
  webkitExitFullscreen?: () => Promise<void>
  mozFullScreenElement?: Element
  msFullscreenElement?: Element
  webkitFullscreenElement?: Element
  msRequestFullscreen?: () => Promise<void>
  mozRequestFullscreen?: () => Promise<void>
  webkitRequestFullscreen?: () => Promise<void>
};


@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.css'],
  standalone: false
})
export class HeaderComponent implements OnInit, OnDestroy {
  @Input() layoutType = '';
  isLoggedIn$: Observable<boolean>;
  public collapsed: boolean;

  vm: any = {};
  modeOperations: boolean;
  showMessages = false;
  version = environment.appVersion;
  ngVersion = environment.ngVersion;
  userName = '';
  topbarChatItems: ChatPreviewItem[] = [];
  unreadMessagesCount = 0;
  topNavCollapsed = false;
  user: SysUserModel;
  element!: FullScreenTypes;
  private document: any;

  languages: Language[] = [];
  selectedLanguage?: Language;
  public supportSite = this.appConfig.supportSite;
  avatarUrl = 'assets/images/users/avatar-2.jpg';
  userDisplayName = '';
  userTitle = '';
  isOnChatPage = false;
  showUnreadCountOnIcon = false;
  themeIcon = 'ri-moon-line';
  companies: CompanyAccessModel[] = [];
  switchingCompanyId: number | null = null;
  @ViewChild('closeCompanySwitcherButton') closeCompanySwitcherButton: ElementRef;

  private subscription: Subscription;
  private fullscreenListener: () => void;
  private readonly destroy$ = new Subject<void>();
  private currentUserId = '';

  // store = inject(Store);
  render = inject(Renderer2);

  @Output() mobileMenuButtonClicked = new EventEmitter();

  constructor(
    private authService: AuthService,
    private storage: StorageService,
    private tableDataService: DataService,
    public p: PermissionsService,
    private appConfig: AppConfigService,
    private notificationService: NotificationService,
    private localStorage: StorageService,
    private eventService: EventService,
    private chatService: ChatService,
    private router: Router
  ) {
    this.document = inject(DOCUMENT);
    this.subscription = this.eventService.eventFilteredByName('TestEvent').subscribe(event => {
      console.log(event.description); // 'some data'
    });
  }

  ngOnInit(): void {
    this.element = document.documentElement;

    this.fullscreenListener = () => this.updateFullscreenClass();
    this.document.addEventListener('fullscreenchange', this.fullscreenListener);
    this.document.addEventListener('webkitfullscreenchange', this.fullscreenListener);
    this.document.addEventListener('mozfullscreenchange', this.fullscreenListener);
    this.document.addEventListener('MSFullscreenChange', this.fullscreenListener);

    this.user = JSON.parse(localStorage.getItem('currentUser'));

    this.vm.Preferences = JSON.parse(this.localStorage.StorageGet('Preferences'));

    this.userName = this.user.name;
    this.avatarUrl = this.buildAvatarUrl();
    this.userDisplayName = this.buildUserDisplayName();
    this.userTitle = (this.user as any)?.workTitle || '?';
    console.log('[header] currentUser (login/switch-company payload):', this.user);
    console.log('[header] currentUser.companies:', this.user?.companies);
    this.companies = this.user?.companies || [];
    // TEMP (testing): fall back to the full my-companies list when the login response
    // didn't include one (e.g. single-company accounts), so the switcher can still be
    // used to exercise the switch-company/mini-re-login flow during testing.
    if (this.companies.length === 0) {
      this.authService.getMyCompanies().then((list) => {
        console.log('[header] my-companies fallback list:', list);
        this.companies = list || [];
      }).catch(() => {
        this.companies = [];
      });
    }

    this.eventService.eventFilteredByName('PreferencesUpdated').pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.vm.Preferences = JSON.parse(this.localStorage.StorageGet('Preferences'));
      this.avatarUrl = this.buildAvatarUrl();
    });

    if (this.vm.Preferences.QuickLinks) {
      this.vm.quickLinks = this.vm.Preferences.QuickLinks;
    } else {
      this.vm.quickLinks = [];
    }

    // this will run on init and act to carry the setting around from page to page
    if (localStorage.getItem('sidePanelStatus') === 'true') {
      this.collapsed = true;
    } else {
      this.collapsed = false;
      // just in case it return a null set it for next check
      // localStorage.setItem('sidePanelStatus', String(this.collapsed));
    }
    // get supported languages
    this.fetchLanguages();
    this.getCompanyInfo();

    const userId = this.getCurrentUserId();
    if (userId) {
      this.currentUserId = userId;
      if (this.chatService.threads().length === 0) {
        this.chatService.loadThreads(userId);
      }

      this.chatService.getTopbarPreviewItems$()
        .pipe(
          observeOn(asyncScheduler),
          takeUntil(this.destroy$)
        )
        .subscribe((items) => {
          this.topbarChatItems = (items || []).map((item) => ({
            ...item,
            unreadCount: Math.max(0, Number(item?.unreadCount ?? 0))
          }));
        });
      this.chatService.getUnreadCount$()
        .pipe(
          observeOn(asyncScheduler),
          takeUntil(this.destroy$)
        )
        .subscribe((count) => {
          this.unreadMessagesCount = Math.max(0, Number(count ?? 0));
          this.showUnreadCountOnIcon = this.unreadMessagesCount > 0 && !this.isOnChatPage;
        });

      this.initializeUnreadCountRefresh(userId);
    }

    // Update route-derived state once on init and on every navigation
    this.updateRouteState(this.router.url);
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd), takeUntil(this.destroy$))
      .subscribe(e => this.updateRouteState(e.urlAfterRedirects));

    // Set initial theme icon
    const theme = document.body.getAttribute('data-bs-theme');
    this.themeIcon = theme === 'dark' ? 'ri-sun-line' : 'ri-moon-line';
  }

  get unreadMessagesBadgeLabel(): string {
    if (this.unreadMessagesCount > 99) {
      return '99+';
    }

    return String(this.unreadMessagesCount);
  }

  private updateRouteState(url: string): void {
    this.isOnChatPage = url.startsWith('/chat');
    this.showUnreadCountOnIcon = this.unreadMessagesCount > 0 && !this.isOnChatPage;
  }

  // Change Theme
  changeTheme() {
    const color = document.body.getAttribute('data-bs-theme');
    const newColor = color === 'dark' ? 'light' : 'dark';
    this.render.setAttribute(document.body, 'data-bs-theme', newColor);
    localStorage.setItem('theme', newColor);
    this.themeIcon = newColor === 'dark' ? 'ri-sun-line' : 'ri-moon-line';
  }

  // set Fullscreen
  fullscreen() {
    if (
      !this.document.fullscreenElement &&
      !this.document.mozFullScreenElement &&
      !this.document.webkitFullscreenElement &&
      !this.document.msFullscreenElement
    ) {
      if (this.element.requestFullscreen) {
        this.element.requestFullscreen().then(r => {
        });
      } else if (this.element.mozRequestFullScreen) {
        /* Firefox */
        this.element.mozRequestFullScreen().then(r => {
        });
      } else if (this.element.webkitRequestFullscreen) {
        /* Chrome, Safari and Opera */
        this.element.webkitRequestFullscreen().then(r => {
        });
      } else if (this.element.msRequestFullscreen) {
        /* IE/Edge */
        this.element.msRequestFullscreen().then(r => {
        });
      }
    } else {
      if (this.document.exitFullscreen) {
        this.document.exitFullscreen().then(r => {
        });
      } else if (this.document.mozCancelFullScreen) {
        /* Firefox */
        this.document.mozCancelFullScreen().then(r => {
        });
      } else if (this.document.webkitExitFullscreen) {
        /* Chrome, Safari and Opera */
        this.document.webkitExitFullscreen().then(r => {
        });
      } else if (this.document.msExitFullscreen) {
        /* IE/Edge */
        this.document.msExitFullscreen().then(r => {
        });
      }
    }
  }

  private updateFullscreenClass() {
    if (
      this.document.fullscreenElement ||
      this.document.mozFullScreenElement ||
      this.document.webkitFullscreenElement ||
      this.document.msFullscreenElement
    ) {
      document.body.classList.add('fullscreen-enable');
    } else {
      document.body.classList.remove('fullscreen-enable');
    }
  }

  getCompanyInfo() {
    this.tableDataService.getApiListData('company?state=active&pageNumber=0&pageSize=999999').subscribe((result: ResponseModel) => {
      this.vm.company = (result.Data ?? []).slice().sort((a: any, b: any) =>
        (a.name ?? '').localeCompare(b.name ?? '')
      );
      // Cache the resolved, human-readable company name so other parts of the app that
      // can't easily resolve it themselves (e.g. ReportPrintService, which builds printed/
      // emailed report headers synchronously) can display the real company name instead of
      // falling back to the raw database name.
      const name = this.currentCompanyName;
      if (name) {
        localStorage.setItem('currentCompanyName', name);
      }
    });
  }

  async logout() {
    await this.authService.logout();
  }

  switchNav() {
    this.modeOperations = !this.modeOperations;
    localStorage.setItem('modeOperations', String(this.modeOperations));
  }

  getDbName() {
    // return this.storage.StorageGet('uDb');
    return this.user.companyDbName;
  }

  getApiVersion() {
    // return this.storage.StorageGet('uDb');
    return this.user.apiVersion;
  }

  get hasMultipleCompanies(): boolean {
    // TEMP (testing): show switcher whenever the user has at least one company entry,
    // so the switch-company flow can be exercised even for accounts with a single company.
    return (this.companies?.length || 0) > 0;
  }

  /**
   * Display name of the company whose database is currently active, for showing in the
   * topbar. The `companies`/`my-companies` payload only carries `companyDatabaseName`
   * (the actual database name, not a friendly name), so it is NOT a reliable source for
   * the human-readable company name (this is also what was used for the GL import's
   * `company_name` lookup, so it must match the real `name`, not the db/dba name). The
   * `company` table info loaded via `getCompanyInfo()` for the active database has the
   * authoritative `name` field, so it's checked first; the `companies` list and the raw
   * database name are only used as a last-resort fallback.
   */
  get currentCompanyName(): string {
    const dbName = this.getDbName();
    const companyInfo = (this.vm.company || [])[0];
    if (companyInfo?.name) {
      return companyInfo.name;
    }
    const match = (this.companies || []).find(c => this.getCompanyDatabaseName(c) === dbName);
    if (match) {
      const name = this.getCompanyDisplayName(match);
      if (name) {
        return name;
      }
    }
    return dbName || '';
  }

  /**
   * Display name for a company entry in the Switch Company modal list. For the currently
   * active company we have the authoritative `name` (via `currentCompanyName`/`getCompanyInfo()`);
   * for other companies in the list, we only have what the `companies`/`my-companies` payload
   * provides (ideally a real `name`, falling back to `companyDatabaseName` if the backend
   * hasn't been updated to include one).
   */
  getCompanyDisplayNameForSwitcher(c: any): string {
    if (this.getCompanyDatabaseName(c) === this.getDbName()) {
      const current = this.currentCompanyName;
      if (current) {
        return current;
      }
    }
    return this.getCompanyDisplayName(c);
  }

  /**
   * Defensive accessor for a company's display name. The backend contract specifies
   * `companyDatabaseName`, but this tolerates alternate casing/naming (e.g. `companyName`,
   * `name`, PascalCase variants) in case the API response doesn't exactly match, so the
   * switcher never renders a blank name.
   */
  getCompanyDisplayName(c: any): string {
    if (!c) {
      return '';
    }
    return c.name
      || c.Name
      || c.companyName
      || c.CompanyName
      || c.companyDatabaseName
      || c.CompanyDatabaseName
      || '';
  }

  /**
   * Returns the actual database name for a company entry (distinct from the human-readable
   * `CompanyName`), used to compare against `getDbName()` (the active connection's database
   * name) so the "Current" badge highlights the right entry.
   */
  getCompanyDatabaseName(c: any): string {
    if (!c) {
      return '';
    }
    return c.companyDatabaseName
      || c.CompanyDatabaseName
      || '';
  }

  /**
   * Defensive accessor for a company's numeric id. The backend contract specifies
   * `companyDatabaseId` (camelCase), but this tolerates PascalCase (`CompanyDatabaseId`)
   * in case the API response doesn't exactly match — otherwise `switchToCompany` would be
   * called with `undefined`, producing an empty `{}` request body to `switch-company`.
   */
  getCompanyId(c: any): number | undefined {
    if (!c) {
      return undefined;
    }
    return c.companyDatabaseId ?? c.CompanyDatabaseId;
  }

  /**
   * Defensive accessor for a company's last-accessed/last-logged-in timestamp. The backend
   * contract specifies `lastAccessedAt` (camelCase), but this tolerates PascalCase
   * (`LastAccessedAt`) in case the API response doesn't exactly match, so the "last used"
   * date in the Switch Company modal doesn't silently fall back to "Never used".
   */
  getCompanyLastAccessed(c: any): string | null {
    if (!c) {
      return null;
    }
    return c.lastAccessedAt || c.LastAccessedAt || null;
  }

  async switchToCompany(companyDatabaseId: number): Promise<void> {
    if (this.switchingCompanyId) {
      return;
    }
    this.switchingCompanyId = companyDatabaseId;
    this.closeCompanySwitcherModal();
    const ok = await this.authService.switchCompany(companyDatabaseId);
    this.switchingCompanyId = null;
    if (ok) {
      this.router.navigate(['/home']);
    }
  }

  private closeCompanySwitcherModal(): void {
    const modalEl = this.closeCompanySwitcherButton?.nativeElement?.closest('.modal');
    if (modalEl) {
      const modal = Modal.getInstance(modalEl) || new Modal(modalEl);
      modal.hide();
    }
  }

  protected buildUserDisplayName(): string {
    const u = this.user as any;
    const first = u?.firstName || '';
    const last = u?.lastName || '';
    if (first || last) {
      return `${first} ${last}`.trim();
    }
    return this.user?.name || '?';
  }

  protected buildAvatarUrl(): string {
    const avatarRelativePath = this.vm?.Preferences?.avatarUrl;
    // The backend returns a placeholder path (e.g. "default-avatar.png") for users
    // who haven't uploaded a custom avatar yet. That placeholder file does not
    // actually exist on the avatars storage server, so requesting it 404s and the
    // image appears "missing" (this is what happens on a user's very first login).
    // Once an avatar is uploaded (or a different company/user context is active),
    // a real relative path is returned and loads fine. Treat the placeholder the
    // same as "no avatar" and fall back to the local default image instead of
    // hitting the remote server for a file that isn't there.
    if (avatarRelativePath && !/default-avatar\.(png|jpg|jpeg|gif|svg)$/i.test(avatarRelativePath)) {
      const prefix = (this.appConfig.avatarsStorageUrlPrefix || '').replace(/\/$/, '');
      const cleanPath = avatarRelativePath.replace(/^\/uploads/, '');
      const relativePath = cleanPath.startsWith('/') ? cleanPath : `/${cleanPath}`;
      return `${prefix}${relativePath}`;
    }
    return 'assets/images/users/avatar-2.jpg';
  }

  getAvatarUrl() {
    return this.avatarUrl;
  }

  onAvatarError(event: Event): void {
    const img = event.target as HTMLImageElement;
    if (img && img.src.indexOf('assets/images/users/avatar-2.jpg') === -1) {
      img.src = 'assets/images/users/avatar-2.jpg';
    }
  }

  openChatThread(threadId: string): void {
    if (!threadId) {
      return;
    }

    const userId = this.getCurrentUserId();
    if (userId) {
      this.chatService.markThreadAsRead(threadId, userId);
    }

    this.router.navigate(['/chat', threadId]);
  }

  markAllMessagesAsRead(): void {
    const userId = this.getCurrentUserId();
    if (!userId) {
      return;
    }

    this.chatService.markAllAsRead(userId);
  }

  private initializeUnreadCountRefresh(userId: string): void {
    merge(
      timer(0, 30000),
      fromEvent(window, 'focus')
    )
      .pipe(
        switchMap(() => this.chatService.refreshUnreadCount(userId)),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  formatTopbarTime(value: string): string {
    if (!value) {
      return '';
    }

    const date = new Date(value);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) {
      return 'now';
    }
    if (diffMin < 60) {
      return `${diffMin} min ago`;
    }
    const diffHours = Math.floor(diffMin / 60);
    if (diffHours < 24) {
      return `${diffHours} hr ago`;
    }
    return date.toLocaleDateString();
  }

  private getCurrentUserId(): string {
    return (this.user?.id ?? this.user?.userId ?? '').toString();
  }

  toggleSidebarWidth(): void {

    if (
      document.body.hasAttribute('data-leftbar-compact-mode') ||
      document.body.getAttribute('data-leftbar-compact-mode') ===
        'condensed' && !document.body.classList.contains('sidebar-enable')
    ) {
      document.body.removeAttribute('data-leftbar-compact-mode');
      document.body.classList.toggle('hide-menu');
      document.body.classList.toggle('sidebar-enable');
    }else{
      document.body.setAttribute('data-leftbar-compact-mode', 'condensed');
      document.body.classList.toggle('sidebar-enable');
    }
  }

  /**
   * Toggle the menu bar when having mobile screen
   */
  toggleMobileMenu(event: any): void {
    this.topNavCollapsed = !this.topNavCollapsed;
    event.preventDefault();
    this.mobileMenuButtonClicked.emit();
  }

  changeLanguage(language: Language) {
    this.selectedLanguage = language;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    if (this.fullscreenListener) {
      this.document.removeEventListener('fullscreenchange', this.fullscreenListener);
      this.document.removeEventListener('webkitfullscreenchange', this.fullscreenListener);
      this.document.removeEventListener('mozfullscreenchange', this.fullscreenListener);
      this.document.removeEventListener('MSFullscreenChange', this.fullscreenListener);
    }
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  fetchLanguages(): void {
    this.languages = [{
      id: 1,
      name: 'English',
      flag: 'assets/images/flags/us.jpg',
    },
      {
        id: 2,
        name: 'Spanish',
        flag: 'assets/images/flags/spain.jpg',
      },
      {
        id: 3,
        name: 'French',
        flag: 'assets/images/flags/french.jpg',
      },
      {
        id: 4,
        name: 'German',
        flag: 'assets/images/flags/germany.jpg',
      },
      {
        id: 5,
        name: 'Italian',
        flag: 'assets/images/flags/italy.jpg',
      }];
    this.selectedLanguage = this.languages[0];
  }
}
